import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import { BioscopeContract } from '@/components/molecules/BioscopeContract/BioscopeContract';
import ContentBlock from '@/components/molecules/ContentBlock';
import { Disclosure } from '@/components/molecules/Disclosure';
import SomethingHappened from '@/components/SomethingHappened';
import type {
    Contract,
    CreateNewContractInput,
    CreateNewContractLicenseInput,
    LicenseInfo,
} from '@/domain/licenses';

import ModalCreateBioscopeContract, {
    type NewContractFormValues,
} from '../ModalCreateBioscopeContract';

import styles from './BioscopeContractList.module.scss';

/**
 * The writes are fired and forgotten: the container reports a failure through `error`, so none
 * of them hands back a promise that could reject unhandled.
 */
export interface BioscopeLicenseListProps {
    contractList: Contract[];
    licenseList: LicenseInfo[];
    isLoading: boolean;
    error: unknown | null;
    onCreateLicense: (contractId: number, newLicense: CreateNewContractLicenseInput) => void;
    onCreateContract: (newContract: NewContractFormValues) => void;
    onUpdateContract: (id: number, contract: CreateNewContractInput) => void;
    onUpdateLicense: (
        contractId: number,
        contractLicenseId: number,
        license: CreateNewContractLicenseInput
    ) => void;
    onDeleteLicense: (contractId: number, contractLicenseId: number) => Promise<unknown>;
    onArchiveContract: (id: number) => Promise<unknown>;
    onUnarchiveContract: (id: number) => Promise<unknown>;
    openMoveModal: (bioscopeId: number) => void;
}

function sortContractsByCreatedAt(contractA: Contract, contractB: Contract): number {
    const createdAtA = new Date(contractA.createdAt).getTime();
    const createdAtB = new Date(contractB.createdAt).getTime();

    if (Number.isNaN(createdAtA) || Number.isNaN(createdAtB)) {
        return contractB.id - contractA.id;
    }

    return createdAtB - createdAtA;
}

export function BioscopeContractList({
    contractList,
    licenseList,
    isLoading,
    error,
    onCreateLicense,
    onCreateContract,
    onUpdateContract,
    onUpdateLicense,
    onDeleteLicense,
    onArchiveContract,
    onUnarchiveContract,
    openMoveModal,
}: BioscopeLicenseListProps) {
    const { t } = useTranslation();
    const activeContracts = useMemo(
        () =>
            [...contractList]
                .filter((contract) => !contract.archivedAt)
                .sort(sortContractsByCreatedAt),
        [contractList]
    );
    const archivedContracts = useMemo(
        () =>
            [...contractList]
                .filter((contract) => contract.archivedAt)
                .sort(sortContractsByCreatedAt),
        [contractList]
    );

    const renderContractBlock = (contract: Contract, isArchived: boolean) => {
        const title = isArchived
            ? `${t(contract.description)} · ${t('bioscopeContracts.contract.archived')}`
            : t(contract.description);

        return (
            <Disclosure key={contract.id} title={title} spacing="none">
                <BioscopeContract
                    contract={contract}
                    licenseList={licenseList}
                    onCreateLicense={(input) => onCreateLicense(contract.id, input)}
                    onUpdateLicense={(contractLicenseId, input) =>
                        onUpdateLicense(contract.id, contractLicenseId, input)
                    }
                    onDeleteLicense={(contractLicenseId) =>
                        onDeleteLicense(contract.id, contractLicenseId)
                    }
                    onArchiveContract={onArchiveContract}
                    onUnarchiveContract={onUnarchiveContract}
                    openMoveModal={openMoveModal}
                    onUpdateContract={onUpdateContract}
                    isArchived={isArchived}
                />
            </Disclosure>
        );
    };

    const activeContent = (
        <div className={styles.contractsList}>
            {activeContracts.map((contract) => renderContractBlock(contract, false))}
        </div>
    );

    const archivedContent = (
        <div className={styles.contractsList}>
            {archivedContracts.map((contract) => renderContractBlock(contract, true))}
        </div>
    );

    return (
        <ContentBlock title={t('bioscopeContracts.list.title')}>
            {error ? (
                <SomethingHappened />
            ) : isLoading ? (
                <Spinner size="lg" />
            ) : (
                <>
                    <Disclosure
                        title={t('bioscopeContracts.list.activeContracts', {
                            count: activeContracts.length,
                        })}
                        defaultOpen>
                        {activeContent}
                    </Disclosure>
                    {archivedContracts.length > 0 && (
                        <Disclosure
                            title={t('bioscopeContracts.list.archivedContracts', {
                                count: archivedContracts.length,
                            })}
                            spacing="top-and-bottom">
                            {archivedContent}
                        </Disclosure>
                    )}
                    <div className={styles.addContractAction}>
                        <ModalCreateBioscopeContract onSubmitted={onCreateContract} />
                    </div>
                </>
            )}
        </ContentBlock>
    );
}
